/**
 * 週次MTG 画面の純粋な組み立て（区分ごとの列・開催回の選択肢・変換の自動入力・URL の読み取り）。
 *
 * **変換の自動入力は機械的な写しだけにする**（AI は使わない）。人が全項目を直してから登録する前提で、
 * 同じ投稿からは毎回同じ初期値になる。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 週次MTG 画面の区分ごとの列・開催回の選択肢・改善要望／組織課題への自動入力・URL の読み取りを組み立てる
 * @graph-connects none
 */

import type { Board } from '@cortex/domain-org-issues';
import type { Category, Post, Series } from '@cortex/domain-team-meeting';
import type { PostView, SeriesDetail } from './api/team-meetings.js';
import type { OrgIssuePeople, TeamMeetingPostSource } from './api/org-issues.js';

/** 日付（YYYY-MM-DD）。 @graph-connects none */
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/** 本文テンプレートの「背景」の見出し。 @graph-connects none */
const BACKGROUND_HEADING = /^##[ \t]*背景[ \t]*$/m;

/** 区分 1 列。 @graph-connects none */
export interface CategoryColumn {
  category: Category;
  posts: PostView[];
}

/**
 * 投稿を区分ごとの列に分ける（区分の並び順・列の中は投稿の古い順＝API の並びのまま）。
 * シリーズに無い区分の投稿は、その区分 ID を名前にした列を後ろに足して出す（黙って消さない）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business Group meeting posts into columns by category in the series order without dropping unknown categories
 * @graph-connects none
 */
export function toCategoryColumns(
  categories: readonly Category[],
  posts: readonly PostView[],
): CategoryColumn[] {
  const sorted = [...categories].sort((a, b) => a.position - b.position);
  const unknown = [...new Set(posts.map((v) => v.post.category))].filter(
    (id) => !sorted.some((c) => c.id === id),
  );
  return [
    ...sorted,
    ...unknown.map((id, i) => ({ id, label: id, position: sorted.length + i })),
  ].map((category) => ({
    category,
    posts: posts.filter((v) => v.post.category === category.id),
  }));
}

/** 開催回の選択肢 1 つ。 @graph-connects none */
export interface MeetingOption {
  meetingId: string;
  /** まだ棚卸ししていない今の開催回 */
  isUpcoming: boolean;
}

/**
 * 開催回の選択肢（新しい順）。今の開催回がまだ作られていなければ先頭に足す。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business List the meetings to switch between, adding the current meeting before its first triage
 * @graph-connects none
 */
export function meetingOptions(detail: Pick<SeriesDetail, 'meetings' | 'currentMeetingOn'>) {
  const options: MeetingOption[] = detail.meetings.map((m) => ({
    meetingId: m.id,
    isUpcoming: false,
  }));
  const current = detail.currentMeetingOn;
  if (!options.some((o) => o.meetingId === current)) {
    options.unshift({ meetingId: current, isUpcoming: true });
  }
  return options;
}

/** 本文の最初の空でない行。 @graph-connects none */
function firstLine(body: string): string {
  return (
    body
      .split('\n')
      .map((line) => line.trim())
      .find((line) => line !== '') ?? ''
  );
}

/** `YYYY-MM-DD` を `YYYY/MM/DD` にする（理由に書く日付）。 @graph-connects none */
function slashDate(date: string): string {
  return date.split('-').join('/');
}

/** 起票する投稿と、棚卸しする開催回（起票は MTG の中で決めるので、その回を指す）。 @graph-connects none */
export interface PostInMeeting {
  post: Pick<Post, 'seriesId' | 'id'>;
  meetingId: string;
}

/**
 * 投稿の URL（棚卸しする開催回に、その投稿の位置つきで戻れる）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business Build the URL that opens the meeting where a post is triaged, at that post
 * @graph-connects none
 */
export function postUrl(origin: string, { post, meetingId }: PostInMeeting): string {
  const meeting = encodeURIComponent(meetingId);
  return `${origin}/team-meetings/${encodeURIComponent(post.seriesId)}?meeting=${meeting}#post-${encodeURIComponent(post.id)}`;
}

/**
 * 起票側に付ける起点（投稿と、棚卸しする開催回を指す）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business Build the source that points an improvement request or issue back to a meeting post
 * @graph-connects none
 */
export function postSource({ post, meetingId }: PostInMeeting): TeamMeetingPostSource {
  return { kind: 'team_meeting_post', seriesId: post.seriesId, meetingId, postId: post.id };
}

/**
 * 「改善要望にする」の初期値。要約 = 本文 1 行目、内容 = 本文、
 * 理由 = `<シリーズ名>（<開催日>）の投稿より` + 投稿の URL、画面 = `whole`。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business Prefill an improvement request from a meeting post by copying its text mechanically
 * @graph-connects postSource [calls] 起点
 */
export function improvementInitial(input: {
  series: Series;
  post: Post;
  meetingId: string;
  url: string;
}) {
  const { series, post, meetingId, url } = input;
  return {
    area: 'whole' as const,
    title: firstLine(post.body),
    body: post.body,
    reason: `${series.name}（${slashDate(meetingId)}）の投稿より\n${url}`,
    source: postSource({ post, meetingId }),
  };
}

/**
 * 「チーム課題にする」の初期値。タイトル = 本文 1 行目、本文 = ボードのテンプレートの「背景」に
 * 投稿の本文と投稿へのリンクを入れたもの（テンプレートに「背景」が無ければ先頭に足す）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business Prefill an organization issue from a meeting post by putting the post into the background section of the board template
 * @graph-connects none
 */
export function issueInitial(input: {
  board: Pick<Board, 'bodyTemplate'>;
  series: Series;
  post: Post;
  meetingId: string;
  url: string;
}) {
  const { board, series, post, meetingId, url } = input;
  const background = `${post.body.trim()}\n\n（${series.name}（${slashDate(meetingId)}）の投稿: ${url}）`;
  const template = board.bodyTemplate;
  const body = BACKGROUND_HEADING.test(template)
    ? template.replace(BACKGROUND_HEADING, (heading) => `${heading}\n\n${background}`)
    : `## 背景\n\n${background}\n\n${template}`;
  return { title: firstLine(post.body), body: body.trimEnd() };
}

/** 開催回の画面の URL の検索部分。 @graph-connects none */
export interface MeetingSearch {
  /** 開催日（YYYY-MM-DD）。省くと今の開催回 */
  meeting?: string;
}

/**
 * URL の `?meeting=` を読む（日付の形でなければ無視して今の開催回を開く）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business Read the meeting date from the meeting page URL, ignoring malformed values
 * @graph-connects none
 */
export function parseMeetingSearch(raw: Record<string, unknown>): MeetingSearch {
  const meeting = raw['meeting'];
  return typeof meeting === 'string' && DATE_PATTERN.test(meeting) ? { meeting } : {};
}

/**
 * ログイン中の人の `users.id` を、返りの人の表からメールで引く。表に居なければ `null`
 * （まだ投稿もいいねもしていない人。いいね済みでないと扱えば足りる）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business Find the signed-in member's users id in the people table of a response by email
 * @graph-connects none
 */
export function viewerIdOf(people: OrgIssuePeople, email: string | undefined): string | null {
  if (email === undefined || email === '') return null;
  const lower = email.toLowerCase();
  return Object.values(people).find((p) => p.email.toLowerCase() === lower)?.id ?? null;
}
